/**
 * apps/command/src/screens/TechnicianQueue.tsx — §15.7's technician view, and
 * the home a `technician` account lands on.
 *
 * "Technician view — the queue. Assigned alerts first, kiosk health,
 * diagnostics, rollout state."
 *
 * P1 delivers the queue and the health it is derived from. Diagnostics and
 * rollout arrive with the remote-commands and update work, and are absent
 * rather than stubbed — §15.7's rule against showing an official a control that
 * can only fail applies to a whole panel as much as to a button.
 *
 * A technician does NOT hold `mbt.command.issue` (D-MBT-04, tonight's owner
 * directive), so this screen deliberately offers no way to touch a machine. It
 * tells them what is wrong and which kiosk; acting on it is a governor's or an
 * operator's call in P1.
 */
import { useCallback } from 'react';
import { useSession } from '../auth/SessionContext.js';
import { AlertFeed } from '../components/AlertFeed.jsx';
import { KioskTile } from '../components/KioskTile.jsx';
import { t } from '../i18n/index.js';
import { statusOf, useFleet } from '../realtime/useFleet.js';
import { EmptyState, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';

export function TechnicianQueue(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);

  if (fleet.error === 'forbidden') return <Notice tone="crit">{t('error.forbidden')}</Notice>;

  // Only the kiosks that need attention. §9.1: "thirty-three green rows teach
  // nobody anything; the two amber ones are the job."
  const needsAttention = fleet.kiosks.filter((kiosk) => {
    const status = statusOf(kiosk, fleet.now);
    return status === 'critical' || status === 'offline' || status === 'warning';
  });

  const needing = fleet.kiosks.filter((kiosk) => {
    const status = statusOf(kiosk, fleet.now);
    return status === 'critical' || status === 'offline' || status === 'warning';
  });
  const lowDisk = fleet.kiosks.filter(
    (kiosk) => (kiosk.telemetry?.health?.disk_free_pct ?? 100) < 20,
  ).length;
  const stuckQueue = fleet.kiosks.filter(
    (kiosk) => (kiosk.telemetry?.work?.queue_depth ?? 0) > 0,
  ).length;
  const inMaintenance = fleet.kiosks.filter(
    (kiosk) => statusOf(kiosk, fleet.now) === 'maintenance',
  ).length;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('queue.eyebrow')}</span>}
        title={t('nav.queue')}
        subtitle={t('queue.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {needing.length}
          </p>
        }
      />

      {/*
        A TECHNICIAN'S FOUR NUMBERS, in the order they decide a route.

        Not the operator's counters: a technician does not care how many kiosks
        are healthy, they care what they have to carry and where. Low disk is a
        visit with a plan; a stuck queue is a visit that has to happen before
        the fourteen-day alarm; and maintenance is the count of machines
        somebody has already claimed, so two people are not dispatched to one
        building.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('queue.kpiNeeding')}
          value={String(needing.length)}
          absent={t('app.notYet')}
          tone={needing.length > 0 ? 'warn' : 'ok'}
          footerLabel={t('status.critical')}
          footerValue={String(
            fleet.kiosks.filter((kiosk) => statusOf(kiosk, fleet.now) === 'critical').length,
          )}
        />
        <KpiCard
          label={t('queue.kpiLowDisk')}
          value={String(lowDisk)}
          absent={t('app.notYet')}
          spread={fleet.kiosks
            .map((kiosk) => kiosk.telemetry?.health?.disk_free_pct)
            .filter((value): value is number => typeof value === 'number')}
          tone={lowDisk > 0 ? 'crit' : 'ok'}
          footerLabel={t('queue.kpiThreshold')}
          footerValue="20%"
        />
        <KpiCard
          label={t('queue.kpiStuck')}
          value={String(stuckQueue)}
          absent={t('app.notYet')}
          tone={stuckQueue > 0 ? 'warn' : 'ok'}
          footerLabel={t('overview.queueTotal')}
          footerValue={String(
            fleet.kiosks.reduce((sum, k) => sum + (k.telemetry?.work?.queue_depth ?? 0), 0),
          )}
        />
        <KpiCard
          label={t('status.maintenance')}
          value={String(inMaintenance)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('queue.kpiClaimed')}
          footerValue=""
        />
      </div>

      <AlertFeed onExpired={onExpired} />

      <Panel>
        <PanelHeader
          title={t('queue.needsAttention')}
          aside={String(needsAttention.length)}
        />
        <div className="p-4">
          {fleet.loading ? (
            <Skeleton className="h-52" />
          ) : needsAttention.length === 0 ? (
            <EmptyState message={t('queue.allHealthy')} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {needsAttention.map((kiosk) => (
                <KioskTile key={kiosk.id} kiosk={kiosk} now={fleet.now} />
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
